// Computes the Firestore position updates after a drag-and-drop.
//
// Given the destination group's current `siblings` (field objects, in visual order),
// the `draggedField`, the `targetField` it was dropped on, and the `edge`
// ('before' | 'after' | 'inside'), returns one update per field in the new order:
//   [{ docid, [positionField]: 1-based-index }]
//
// - Same-list reorder: the dragged field is removed from `siblings` first, then reinserted.
// - Cross-list move: a dragged field not already in `siblings` grows the list.
// - 'inside' or no target: the dragged field is appended to the end.
//
// `positionField` is 'Position' for the top-level list and 'SectionPosition' inside a
// section. The caller is responsible for also writing SectionID when the field changes
// group — this helper only computes ordering.
//
// AI-CONTEXT: See docs/features/questionnaire-sections.md (T1 foundation helpers)
const reorderFields = ({ siblings, draggedField, targetField, edge, positionField = 'Position' }) => {
    if (!draggedField || !Array.isArray(siblings)) return []

    // Destination list without the dragged field (handles same-list reorder cleanly)
    const rest = siblings.filter((f) => f.docid !== draggedField.docid)

    let insertAt = rest.length // default: append (covers 'inside' and no target)
    if (targetField && edge !== 'inside') {
        const targetIndex = rest.findIndex((f) => f.docid === targetField.docid)
        if (targetIndex !== -1) {
            insertAt = edge === 'after' ? targetIndex + 1 : targetIndex
        }
    }

    const ordered = [...rest]
    ordered.splice(insertAt, 0, draggedField)

    return ordered.map((field, index) => ({
        docid: field.docid,
        [positionField]: index + 1,
    }))
}

export default reorderFields
